import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, BookmarkPlus, BookmarkCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { findOrCreateBook, isValidUuid, hasValidIdentifier } from '@/lib/bookIdentity';
import { autoCategorizeTBRBook } from '@/lib/autoCategorizeTBR';

interface BookData {
  id?: string;
  open_library_id?: string;
  isbn13?: string;
  isbn10?: string;
  title: string;
  subtitle?: string;
  authors: string[];
  cover_url?: string;
  published_year?: number;
  synopsis?: string;
  isLocal?: boolean;
}

interface ReviewTBRButtonsProps {
  book: BookData;
  className?: string;
  onTBRAdd?: (bookId: string) => void;
  onRatingClick?: (bookId: string) => void;
}

export function ReviewTBRButtons({ book, className = '', onTBRAdd, onRatingClick }: ReviewTBRButtonsProps) {
  const [isInTBR, setIsInTBR] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if book is in TBR on mount
  useEffect(() => {
    if (user && book.id && isValidUuid(book.id)) {
      checkTBRStatus();
    }
  }, [user, book.id]);

  const checkTBRStatus = async () => {
    if (!user || !book.id || !isValidUuid(book.id)) return;
    
    try {
      const { data: userBooks } = await supabase
        .from('user_books')
        .select('shelf')
        .eq('user_id', user.id)
        .eq('book_id', book.id)
        .eq('shelf', 'to-read')
        .maybeSingle();

      setIsInTBR(!!userBooks);
    } catch (error) {
      console.error('Error checking TBR status:', error);
    }
  };

  const handleReview = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      // If it's a local book with valid UUID, navigate directly
      if (book.isLocal && book.id && isValidUuid(book.id)) {
        onRatingClick?.(book.id);
        navigate(`/book/${book.id}`);
        return;
      }

      // Check we have at least one identifier
      if (!hasValidIdentifier(book)) {
        throw new Error('No valid book identifier available');
      }

      // Find or create book using ISBN-first pattern
      const bookId = await findOrCreateBook({
        id: book.id,
        isbn13: book.isbn13,
        isbn10: book.isbn10,
        open_library_id: book.open_library_id,
        title: book.title,
        subtitle: book.subtitle,
        authors: book.authors,
        cover_url: book.cover_url,
        published_year: book.published_year,
        synopsis: book.synopsis,
      });

      onRatingClick?.(bookId);
      navigate(`/book/${bookId}`);
    } catch (error) {
      console.error('Error handling review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Could not open book for review.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTBR = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      // Check we have at least one identifier
      if (!hasValidIdentifier(book)) {
        throw new Error('No valid book identifier available');
      }

      // Find or create book using ISBN-first pattern
      const bookId = await findOrCreateBook({
        id: book.id,
        isbn13: book.isbn13,
        isbn10: book.isbn10,
        open_library_id: book.open_library_id,
        title: book.title,
        subtitle: book.subtitle,
        authors: book.authors,
        cover_url: book.cover_url,
        published_year: book.published_year,
        synopsis: book.synopsis,
      });

      if (isInTBR) {
        // Check if book has been read/rated before removing from TBR
        const { data: existingEntry } = await supabase
          .from('user_books')
          .select('finished_at, rating_star, review_text')
          .eq('user_id', user.id)
          .eq('book_id', bookId)
          .single();

        if (existingEntry && (existingEntry.finished_at || existingEntry.rating_star)) {
          // Don't delete - just update shelf to 'read'
          const updateData: { shelf: string; finished_at?: string } = { shelf: 'read' };
          if (existingEntry.finished_at) {
            updateData.finished_at = existingEntry.finished_at;
          }
          await supabase
            .from('user_books')
            .update(updateData)
            .eq('user_id', user.id)
            .eq('book_id', bookId);

          toast({
            title: "Moved to Read Shelf",
            description: "Book moved from TBR to your Read shelf since you've already read/reviewed it.",
          });
        } else {
          // Safe to delete - no read data exists
          await supabase
            .from('user_books')
            .delete()
            .eq('user_id', user.id)
            .eq('book_id', bookId);

          toast({
            title: "Removed from TBR",
            description: "Book removed from your To Be Read list.",
          });
        }
        
        setIsInTBR(false);
      } else {
        // Add to TBR
        await supabase
          .from('user_books')
          .upsert({
            user_id: user.id,
            book_id: bookId,
            shelf: 'to-read',
            privacy: 'private',
          }, {
            onConflict: 'user_id,book_id'
          });

        setIsInTBR(true);
        toast({
          title: "Added to TBR",
          description: "Book added to your To Be Read list.",
        });

        // Auto-categorize the TBR book
        autoCategorizeTBRBook(user.id, bookId).catch(err => 
          console.warn('Auto-categorization failed:', err)
        );
        onTBRAdd?.(bookId);
      }
    } catch (error) {
      console.error('Error handling TBR:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update your TBR list.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex gap-1.5 mt-2 ${className}`}>
      <Button
        size="sm"
        variant="default"
        onClick={handleReview}
        disabled={loading}
        className="flex-1 h-8 text-xs px-2"
      >
        <Star className="w-3.5 h-3.5 mr-1 shrink-0" />
        <span className="truncate">Review</span>
      </Button>
      
      <Button
        size="sm"
        variant={isInTBR ? "secondary" : "outline"}
        onClick={handleTBR}
        disabled={loading}
        className="flex-1 h-8 text-xs px-2"
      >
        {isInTBR ? (
          <>
            <BookmarkCheck className="w-3.5 h-3.5 mr-1 shrink-0" />
            <span className="truncate">In TBR</span>
          </>
        ) : (
          <>
            <BookmarkPlus className="w-3.5 h-3.5 mr-1 shrink-0" />
            <span className="truncate">TBR</span>
          </>
        )}
      </Button>
    </div>
  );
}

export default memo(ReviewTBRButtons);
